import React from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {Accordion, AccordionItem, AccordionItemHeading, AccordionItemButton, AccordionItemPanel} from 'react-accessible-accordion';
import slugify from 'slugify';
import {MenuLink} from './MenuLink';
import {config} from '../../../../config';
import {NavigationType} from '../../../types/block';
import {getLink} from './SubMenu';
import type {LocaleEnum} from '../../../types/locale';
import {Caret} from '../../icon/Caret';

type AccordionProps = {
  links: Pick<NavigationType, 'submenu'>;
  linkClasses?: string;
  currentPathname?: string;
  title: string;
  bg?: string;
  textColor?: string;
  actionTextColor?: string;
};

const AccordionContent: React.FC<{
  links: JSX.Element[];
}> = props => {
  const content = (
    <ul>
      {props.links.length > 0 &&
        props.links.map((entry, index) => (
          <li key={`${index}-accordion`} className='pt-2'>
            {entry}
          </li>
        ))}
    </ul>
  );

  return content;
};

function isActive(href: string, currentPathname: string): boolean {
  return Boolean(href.replace(config.siteUrl as string, '') === currentPathname);
}

export const AccordionMenu: React.FC<AccordionProps> = props => {
  const [openTabs, setOpenTabs] = React.useState([]);
  const slugifiedTitle = slugify(props.title);

  const intl = useIntl();

  const customColors = {
    background: props.bg ?? 'initial',
    color: props.textColor ?? 'initial'
  };

  if (customColors.background !== 'initial' && openTabs.includes(slugifiedTitle)) {
    customColors.background = customColors.background + '99';
    customColors.color = props.actionTextColor;
  }

  return (
    <Accordion
      allowMultipleExpanded
      allowZeroExpanded
      onChange={tabs => {
        setOpenTabs(tabs);
      }}
    >
      <AccordionItem uuid={slugifiedTitle} key={slugifiedTitle}>
        <AccordionItemHeading style={customColors} className='p-3'>
          <AccordionItemButton className='focus:outline-none cursor-pointer flex items-center py-0' data-testid={`${props.title}-mobile`}>
            <p className='m-0 text-md font-body mr-3'>{props.title}</p>
            <Caret isExpanded={openTabs.includes(slugifiedTitle)} width={14} height={24} inheritColor={customColors.background !== 'initial'} />
          </AccordionItemButton>
        </AccordionItemHeading>
        <AccordionItemPanel className='pb-4' data-testid={`${props.title}-submenu-mobile`}>
          <>
            <AccordionContent
              links={props.links.submenu.map(entry => {
                const link = entry.slug ? entry.slug.slug : entry.customSlug;

                return (
                  <MenuLink className={props.linkClasses} to={getLink(intl.locale as LocaleEnum, entry)} active={isActive(link, props.currentPathname)}>
                    {entry.title}
                    {entry.isNew && (
                      <span className='text-white bg-primary text-xs font-bold rounded-full py-1 px-3 ml-3'>
                        <FormattedMessage id='header.menu.new' />
                      </span>
                    )}
                  </MenuLink>
                );
              })}
            />
          </>
        </AccordionItemPanel>
      </AccordionItem>
    </Accordion>
  );
};
